import cx from 'classnames';
import {
  AnimatePresence,
  DragControls,
  Reorder as ReorderFM,
  useDragControls,
} from 'framer-motion';
import {
  createContext,
  ElementType,
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactHTML,
  ReactNode,
  useContext,
} from 'react';
import css from './Reorder.module.scss';
import { useReorderItemDragStyle } from './hooks';

interface IItemContext {
  controls: DragControls;
}

const ItemContext = createContext<IItemContext>({
  controls: undefined,
});

const useItemContext = () => useContext(ItemContext);

interface IItemContextProviderProps {
  controls: DragControls;
  children: ReactNode;
}

const ItemContextProvider = ({
  children,
  controls,
}: IItemContextProviderProps) => {
  return (
    <ItemContext.Provider value={{ controls }}>{children}</ItemContext.Provider>
  );
};

interface IReorderProps<T> {
  as?: keyof ReactHTML | ReactNode | ForwardRefExoticComponent<any>;
  children: ReactNode;
  className?: string;
  onReorder?: (values: T[]) => void;
  values: T[];
}

const Reorder = <T,>({
  as,
  children,
  className,
  onReorder,
  values,
}: IReorderProps<T>) => {
  return (
    <ReorderFM.Group
      as={as as keyof ReactHTML}
      className={className}
      onReorder={onReorder}
      values={values}
    >
      <AnimatePresence>{children}</AnimatePresence>
    </ReorderFM.Group>
  );
};

interface IItemProps<T> {
  as?: keyof ReactHTML | ReactNode | ForwardRefExoticComponent<any>;
  children: ReactNode;
  className?: string;
  hasDragHandle?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  value: T;
}

const Item = <T,>({
  as,
  children,
  className,
  hasDragHandle,
  onClick,
  onMouseEnter,
  onMouseLeave,
  value,
}: IItemProps<T>) => {
  const controls = useDragControls();

  const style = useReorderItemDragStyle({
    direction: 'y',
    boxShadow: {
      active: '0px 4px 8px 0px rgba(116, 116, 116, 0.2)',
      inactive: '0px 0px 0px rgba(0,0,0,0)',
    },
  });

  return (
    <ItemContextProvider controls={controls}>
      <ReorderFM.Item
        animate={{ opacity: 1 }}
        as={as as keyof ReactHTML}
        className={className}
        dragControls={hasDragHandle ? controls : undefined}
        dragListener={!hasDragHandle}
        dragTransition={{ bounceDamping: 20 }}
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={style}
        transition={{ duration: 0 }}
        value={value}
      >
        {children}
      </ReorderFM.Item>
    </ItemContextProvider>
  );
};

interface IDragHandleProps extends HTMLAttributes<HTMLDivElement> {
  icon: ElementType;
  color?: 'gray';
}

const DragHandle = ({
  className,
  icon: Icon,
  color = 'gray',
  ...rest
}: IDragHandleProps) => {
  const { controls } = useItemContext();

  return (
    <div
      className={cx(css.dragHandle, css[color], className)}
      onPointerDown={(e) => controls.start(e)}
      {...rest}
    >
      <Icon height={16} width={16} />
    </div>
  );
};

Reorder.Item = Item;
Reorder.DragHandle = DragHandle;

export { Reorder };
